import { z } from 'zod';

/**
 * Client — Zod schema + inferred TS type mirroring the wire shape returned
 * by `apps/api/src/resources/ClientResource.ts`. Kept in the FE feature so
 * runtime validation (Zod) and TS types stay co-located with the CRM pages
 * that consume them.
 *
 * Field reference (per `clients` schema in `packages/db/src/schema/crm.ts`):
 *   - `identificationType` is free-form text in the DB (`RIF`, `DNI`,
 *     `CI`, `PASSPORT`). Slice 1 keeps it as a string; the form narrows
 *     to a select of common Venezuelan document types.
 *   - `assignedSellerId` points at `users.id` (GLOBAL). The cross-tenant
 *     seller guard runs on the server (`assertMembershipInTenant`) — the
 *     FE also narrows the dropdown to users with a SELLER role AND
 *     membership in the current tenant.
 *   - `assignedSeller` is an embedded ref (id + display name + email);
 *     the ClientResource builds it via a LEFT JOIN on users + profiles.
 *   - `stateId` / `cityId` point at the GLOBAL geo tables (no RLS). The
 *     route LEFT JOINs `geo_states` + `geo_cities` and exposes the names
 *     (`stateName`, `cityName`) so the FE can render the card without a
 *     second round-trip. All four are nullable.
 *   - `medicalSpecialty` is a locked pgEnum on the DB side; the FE
 *     mirrors the same closed vocabulary so the dropdown stays in sync
 *     with the API. Nullable for historical clients.
 */

export const identificationTypeSchema = z.enum(['RIF', 'DNI', 'CI', 'PASSPORT', 'OTHER']);
export type IdentificationType = z.infer<typeof identificationTypeSchema>;

/**
 * Legacy `identification_type` values found in production that predate
 * the closed vocabulary. The DB column is free-form `text`
 * (`packages/db/src/schema/crm.ts`), so historical rows can hold
 * anything — when this was fixed, 632 rows were `SIN ESPECIFICAR` and
 * 220 were `CEDULA`. Known synonyms normalize to their canonical value.
 */
const IDENTIFICATION_TYPE_ALIASES: Record<string, IdentificationType> = {
  CI: 'CI',
  CEDULA: 'CI',
  'CÉDULA': 'CI',
  RIF: 'RIF',
  DNI: 'DNI',
  PASSPORT: 'PASSPORT',
  PASAPORTE: 'PASSPORT',
  OTHER: 'OTHER',
  OTRO: 'OTHER',
  'SIN ESPECIFICAR': 'OTHER',
};

/**
 * Coerce any stored `identificationType` into the closed vocabulary.
 *
 * Replaces the former `identificationTypeSchema.parse(...)` inside the
 * edit form, which THREW for every out-of-vocabulary row and blanked
 * the whole page (client report: "editar cliente" crashed with a
 * ZodError on `'SIN ESPECIFICAR'`). This never throws: an unknown,
 * empty, or null value falls back to `'OTHER'`.
 */
export function normalizeIdentificationType(raw: string | null | undefined): IdentificationType {
  if (!raw) return 'OTHER';
  const key = raw.trim().normalize('NFC').toUpperCase();
  return IDENTIFICATION_TYPE_ALIASES[key] ?? 'OTHER';
}

export interface IdentificationTypeOption {
  value: IdentificationType;
  label: string;
}

/**
 * Picker options for the "Tipo de ID" select. The id is the wire value
 * (matches `identificationTypeSchema` AND the API enum); the label is
 * what the user sees. `OTHER` is the catch-all for "Otro / Sin
 * especificar" and is also the normalization target for legacy rows.
 */
export const IDENTIFICATION_TYPE_OPTIONS: readonly IdentificationTypeOption[] = [
  { value: 'CI', label: 'CI — Cédula' },
  { value: 'RIF', label: 'RIF' },
  { value: 'DNI', label: 'DNI' },
  { value: 'PASSPORT', label: 'Pasaporte' },
  { value: 'OTHER', label: 'Otro / Sin especificar' },
] as const;

/**
 * Sprint-2 — medical specialty Zod mirror of the DB pgEnum. The
 * closed vocabulary here MUST match `medicalSpecialtyEnum.enumValues`
 * in `packages/db/src/schema/enums.ts` (single source of truth).
 *
 * Keep the labels in sync with the FE picker (`MEDICAL_SPECIALTY_OPTIONS`
 * in the form). The form maps each id → display label so the user
 * sees "Ginecología y Obstetricia" while the wire sends the
 * snake_case id.
 */
export const medicalSpecialtySchema = z.enum([
  'ginecologia_y_obstetricia',
  'cardiologia',
  'ecografista_integral',
  'radiologia',
  'gastroenterologia',
  'centro_medico',
]);
export type MedicalSpecialty = z.infer<typeof medicalSpecialtySchema>;

/**
 * Client negotiation status — Sprint-2 (semáforo CRM).
 *
 * Mirrors `clientStatusEnum.enumValues` in
 * `packages/db/src/schema/enums.ts` (single source of truth on the
 * DB side). The 3 buckets back the color-coded "semáforo" dot on
 * every client card AND the dropdown filter in the FilterBar:
 *
 *   cerrada         → Verde (green)   — sale closed/won (displayed as "Concretado")
 *   en_negociacion  → Amarillo (yellow) — actively in negotiation (DEFAULT)
 *   no_concreto     → Rojo (red)      — negotiation ended without a sale
 *   sin_respuesta   → Naranja (orange) — no response (client request)
 *
 * The DB column is NOT NULL with DEFAULT 'en_negociacion', so every
 * client row has a status from the moment it is created. We still
 * widen the wire shape to `string` (see `clientSchema` below) so a
 * stale FE doesn't crash on a removed value.
 */
export const clientStatusSchema = z.enum([
  'cerrada',
  'en_negociacion',
  'no_concreto',
  'sin_respuesta',
]);
export type ClientStatus = z.infer<typeof clientStatusSchema>;

/**
 * Slice 0 — client origin (Origen) Zod mirror of the DB pgEnum.
 * The closed vocabulary here MUST match
 * `clientOriginEnum.enumValues` in
 * `packages/db/src/schema/enums.ts` (single source of truth).
 */
export const clientOriginSchema = z.enum([
  'instagram',
  'visita_medica',
  'recomendacion_otro_cliente',
  'recomendacion_ceo',
  'feria_comercial',
  'visita_sede',
]);
export type ClientOrigin = z.infer<typeof clientOriginSchema>;

/**
 * Slice 0 — CRM segment tabs. The three segments partition the
 * four negotiation statuses:
 *
 *   ventas_cerradas → `cerrada`
 *   negociacion     → `en_negociacion`
 *   no_concretadas  → `no_concreto` + `sin_respuesta`
 *
 * The wire sends the segment id; the API maps it to the matching
 * `client_status` predicate and AND-combines it with every other
 * filter, so each segment supports the same filter set.
 */
export const clientSegmentSchema = z.enum(['ventas_cerradas', 'negociacion', 'no_concretadas']);
export type ClientSegment = z.infer<typeof clientSegmentSchema>;

/**
 * Wire shape — server returns `identificationType` as a free-form string
 * (the DB column is `text`). We widen the schema to `string` here so the
 * page can render whatever the server sent without crashing on a value
 * that hasn't been added to the enum yet. The form narrows via
 * `identificationTypeSchema` so writes stay normalized.
 *
 * Same widening for `medicalSpecialty` — the schema is locked on the DB
 * side, but a stale FE may receive a value that's been removed server-
 * side. We widen to `string | null` so the page renders "Sin
 * especialidad" instead of crashing the table.
 */
export const clientSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string().min(1),
  name: z.string().min(1).max(200),
  identificationType: z.string(),
  identificationNumber: z.string().min(1).max(40),
  phone: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  address: z.string().nullable().optional(),
  assignedSellerId: z.string().nullable().optional(),
  assignedSeller: z
    .object({
      id: z.string().min(1),
      email: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      fullName: z.string(),
    })
    .nullable()
    .optional(),
  isActive: z.boolean(),
  // Slice 6 PR#5 (CAP-3) — MAX(sales_orders.closed_at) for this client.
  // Null when the client has no closed sales (initial create + clients
  // whose orders have never been closed). The CRM dashboard uses this
  // for the "last activity" widget.
  latest_sale_closed_at: z.string().nullable().optional(),
  // Sprint-2 — geography expansion. All nullable; the route LEFT
  // JOINs `geo_states` + `geo_cities` and exposes the names so the
  // card / detail sheet can render without a second round-trip.
  stateId: z.string().nullable().optional(),
  stateName: z.string().nullable().optional(),
  cityId: z.string().nullable().optional(),
  cityName: z.string().nullable().optional(),
  // Sprint-2 — medical specialty. Locked pgEnum on the DB side;
  // widened to `string` here so a stale FE doesn't crash on a value
  // that's been removed server-side.
  medicalSpecialty: z.string().nullable().optional(),
  // Sprint-2 — negotiation status. NOT NULL on the DB side with
  // DEFAULT 'en_negociacion'; widened to `string` here for the
  // same stale-FE-safety reason as `medicalSpecialty`.
  clientStatus: z.string(),
  // Sprint-2 — fecha de captación (lead capture date). Calendar-day
  // precision in ISO format (`YYYY-MM-DD`) — the same wire shape
  // `<input type="date">` produces. Distinct from `createdAt`
  // (insert timestamp) — the rep picks this when onboarding and
  // may backdate it for historical records.
  fechaCaptacion: z.string().nullable().optional(),
  // Fecha de concreción (client request): day the negotiation was
  // marked Concretado. Same wire shape as fechaCaptacion.
  fechaCierre: z.string().nullable().optional(),
  // Sprint-3 — producto que mostró interés. Nullable FK to
  // `products.id`; the route LEFT JOINs the product and exposes a
  // small `{ id, name, sku }` snapshot so the card / detail /
  // filter UI can render the name without a second round-trip.
  // The snapshot is `null` when the client has no recorded
  // interest (default for historical clients that pre-date the
  // column).
  interestedProductId: z.string().nullable().optional(),
  interestedProduct: z
    .object({
      id: z.string().min(1),
      name: z.string(),
      sku: z.string(),
    })
    .nullable()
    .optional(),
  // Slice 0 — client origin (Origen). Locked pgEnum on the DB
  // side; widened to `string` here so a stale FE doesn't crash on
  // a value that's been removed server-side.
  origen: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Client = z.infer<typeof clientSchema>;

// ---------------------------------------------------------------------------
// Form schemas — drive ClientForm (TanStack Form) validation.
// ---------------------------------------------------------------------------

export const createClientFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  identificationType: identificationTypeSchema,
  identificationNumber: z.string().trim().min(1, 'ID number is required').max(40),
  phone: z.string().trim().max(40).optional().or(z.literal('')),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  address: z.string().trim().max(500).optional().or(z.literal('')),
  /** Empty string means "unassigned". */
  assignedSellerId: z.string().optional().or(z.literal('')),
  isActive: z.boolean(),
  // Sprint-2 — geo + specialty. Empty string means "unset" (the form
  // renders the "Sin clasificar" / "Sin especialidad" placeholder).
  // The wire layer (`toApiInput`) translates `''` → `undefined` so the
  // API receives either a UUID / enum value or omits the key entirely.
  stateId: z.string().optional().or(z.literal('')),
  cityId: z.string().optional().or(z.literal('')),
  medicalSpecialty: medicalSpecialtySchema.optional().or(z.literal('')),
  // Sprint-2 — negotiation status. Closed vocabulary (3 buckets);
  // defaults to 'en_negociacion' on the DB side. The form starts
  // with the same default so an unchanged value still round-trips.
  clientStatus: clientStatusSchema,
  // Sprint-2 — fecha de captación. Calendar-day ISO string
  // (`YYYY-MM-DD`) — `DEFAULT_VALUES` pre-fills TODAY so the rep
  // can hit "Crear cliente" without touching the field. The field
  // is editable so historical clients can be backdated. `null`
  // clears it.
  fechaCaptacion: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida')
    .nullable()
    .optional()
    .or(z.literal('')),
  // Fecha de concreción (client request): required by the form
  // validator (below) when clientStatus is Concretado. Same
  // empty-string-as-unset pattern as fechaCaptacion.
  fechaCierre: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida')
    .nullable()
    .optional()
    .or(z.literal('')),
  // Sprint-3 — producto que mostró interés. Empty string means
  // "no product of interest" (the form renders "Sin producto").
  // The wire layer (`toApiInput`) translates `''` → `undefined`
  // so the API receives either a UUID or omits the key entirely.
  interestedProductId: z.string().optional().or(z.literal('')),
  // Slice 0 — client origin (Origen). Empty string means "unset"
  // (the form renders "Sin origen"). The wire layer translates
  // `''` → `undefined` so the API receives either an enum value
  // or omits the key entirely.
  origen: clientOriginSchema.optional().or(z.literal('')),
});

export type CreateClientFormValues = z.infer<typeof createClientFormSchema>;

export const updateClientFormSchema = createClientFormSchema;
export type UpdateClientFormValues = CreateClientFormValues;

// ---------------------------------------------------------------------------
// Wire-shape input schemas (what the API actually accepts).
// ---------------------------------------------------------------------------

export const createClientInputSchema = z.object({
  name: z.string().min(1).max(200),
  identificationType: z.string().min(1),
  identificationNumber: z.string().min(1).max(40),
  phone: z.string().max(40).optional().nullable(),
  email: z.string().email().optional().nullable(),
  address: z.string().max(500).optional().nullable(),
  assignedSellerId: z.string().optional().nullable(),
  isActive: z.boolean().optional(),
  // Sprint-2 — geo + specialty on the wire.
  stateId: z.string().uuid().optional().nullable(),
  cityId: z.string().uuid().optional().nullable(),
  medicalSpecialty: medicalSpecialtySchema.optional().nullable(),
  // Sprint-2 — negotiation status on the wire. PATCH can pass
  // `null` to reset (the DB default kicks in for fresh rows on
  // POST when the key is omitted).
  clientStatus: clientStatusSchema.optional().nullable(),
  // Sprint-2 — fecha de captación on the wire. ISO date string
  // (`YYYY-MM-DD`) or null. PATCH with `null` clears the field.
  fechaCaptacion: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'fechaCaptacion must be a YYYY-MM-DD date string')
    .nullable()
    .optional(),
  // Fecha de concreción on the wire (client request). Same shape;
  // PATCH with `null` clears the field (the BE still requires it
  // while the status is Concretado).
  fechaCierre: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'fechaCierre must be a YYYY-MM-DD date string')
    .nullable()
    .optional(),
  // Sprint-3 — producto de interés on the wire. UUID or null;
  // PATCH with `null` clears the field (same pattern as
  // `stateId` / `cityId`). The FE form translates empty-string
  // to undefined upstream of this schema.
  interestedProductId: z.string().uuid().nullable().optional(),
  // Slice 0 — client origin (Origen) on the wire. Enum value or
  // null; PATCH with `null` clears the field. The FE form
  // translates empty-string to undefined upstream of this schema.
  origen: clientOriginSchema.nullable().optional(),
});

export type CreateClientInput = z.infer<typeof createClientInputSchema>;

export const updateClientInputSchema = createClientInputSchema
  .partial()
  .refine((obj) => Object.keys(obj).length > 0, { message: 'At least one field must be provided' });

export type UpdateClientInput = z.infer<typeof updateClientInputSchema>;

// ---------------------------------------------------------------------------
// List response — mirrors `apps/api/src/routes/clients.ts#GET /clients`.
// ---------------------------------------------------------------------------

export const clientsListResponseSchema = z.object({
  data: z.array(clientSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});

export type ClientsListResponse = z.infer<typeof clientsListResponseSchema>;

// ---------------------------------------------------------------------------
// Display labels — Sprint-2.
// ---------------------------------------------------------------------------

/**
 * Sprint-2 — medical specialty picker options. The id is the wire
 * value (snake_case, matches the DB pgEnum + the Zod schema); the
 * label is what the user sees in the dropdown. Keep these in sync
 * with `medicalSpecialtySchema` above.
 */
export interface MedicalSpecialtyOption {
  value: MedicalSpecialty;
  label: string;
}

export const MEDICAL_SPECIALTY_OPTIONS: readonly MedicalSpecialtyOption[] = [
  { value: 'ginecologia_y_obstetricia', label: 'Ginecología y Obstetricia' },
  { value: 'cardiologia', label: 'Cardiología' },
  { value: 'ecografista_integral', label: 'Ecografista integral' },
  { value: 'radiologia', label: 'Radiología' },
  { value: 'gastroenterologia', label: 'Gastroenterología' },
  { value: 'centro_medico', label: 'Centro Médico' },
] as const;

/**
 * Sprint-2 — negotiation status picker options. Each entry binds
 * the wire id to a Spanish display label AND the semáforo color
 * the card / detail sheet renders as a colored dot. The color
 * values are raw Tailwind class names so the rendering component
 * can spread them straight onto the `<span>` element without
 * needing a separate lookup.
 *
 * Colors are picked to match the user's spec verbatim:
 *   cerrada         → bg-emerald-500  (Verde)
 *   en_negociacion  → bg-amber-500    (Amarillo)
 *   no_concreto     → bg-red-500      (Rojo)
 */
export interface ClientStatusOption {
  value: ClientStatus;
  label: string;
  /** Tailwind class for the semáforo dot background. */
  dotClass: string;
  /** Tailwind class for the badge background (status pill). */
  badgeClass: string;
}

export const CLIENT_STATUS_OPTIONS: readonly ClientStatusOption[] = [
  {
    value: 'en_negociacion',
    label: 'En negociación',
    dotClass: 'bg-amber-500',
    badgeClass: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  },
  {
    value: 'cerrada',
    label: 'Concretado',
    dotClass: 'bg-emerald-500',
    badgeClass: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  },
  {
    value: 'no_concreto',
    label: 'No concretado',
    dotClass: 'bg-red-500',
    badgeClass: 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30',
  },
  {
    value: 'sin_respuesta',
    label: 'Sin respuesta',
    dotClass: 'bg-orange-500',
    badgeClass: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30',
  },
] as const;

/**
 * Look up the display metadata for a status id. Returns `null`
 * when the id is not in the closed vocabulary (stale FE receiving
 * a removed id from a newer backend). Callers fall back to a
 * neutral grey dot in that case.
 */
export function getClientStatusOption(value: string | null | undefined): ClientStatusOption | null {
  if (!value) return null;
  return CLIENT_STATUS_OPTIONS.find((o) => o.value === value) ?? null;
}

/**
 * Slice 0 — client origin (Origen) picker options. The id is the
 * wire value (snake_case, matches the DB pgEnum + the Zod
 * schema); the label is what the user sees in the dropdown and
 * filter bar. Keep these in sync with `clientOriginSchema`
 * above.
 */
export interface ClientOriginOption {
  value: ClientOrigin;
  label: string;
}

export const CLIENT_ORIGIN_OPTIONS: readonly ClientOriginOption[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'visita_medica', label: 'Visita médica' },
  { value: 'recomendacion_otro_cliente', label: 'Recomendación de otro cliente' },
  { value: 'recomendacion_ceo', label: 'Recomendación del CEO' },
  { value: 'feria_comercial', label: 'Feria comercial' },
  { value: 'visita_sede', label: 'Visita a sede' },
] as const;

/**
 * Map an origin id (wire value, snake_case) to its display
 * label. Returns `null` when the id is not in the closed
 * vocabulary — the caller falls back to rendering the raw id.
 */
export function getClientOriginOption(
  value: string | null | undefined,
): ClientOriginOption | null {
  if (!value) return null;
  return CLIENT_ORIGIN_OPTIONS.find((o) => o.value === value) ?? null;
}

/**
 * Slice 0 — CRM segment tabs. Each entry binds the wire id to a
 * display label. The API maps the id to the matching
 * `client_status` set (see `segmentToStatus` in
 * `apps/api/src/routes/clients.ts`).
 */
export interface ClientSegmentOption {
  value: ClientSegment;
  label: string;
}

export const CLIENT_SEGMENT_OPTIONS: readonly ClientSegmentOption[] = [
  { value: 'ventas_cerradas', label: 'Ventas cerradas' },
  { value: 'negociacion', label: 'Negociación' },
  { value: 'no_concretadas', label: 'No concretadas' },
] as const;

// ---------------------------------------------------------------------------
// Display label helpers — pickers, listing columns, CRM cards.
// ---------------------------------------------------------------------------

/**
 * Primary label for a client row (dropdown option, listing cell,
 * CRM card title). Prefers the human `name`; falls back to the
 * identification number when the stored name is empty, equals the
 * identification type (RIF/CI/DNI), or equals the identification
 * number itself — all canonical placeholder signatures that show
 * up in seeds and ad-hoc admin creates. Without the fallback, a
 * tenant with bad data reads as a list of "RIF" rows and the
 * admin cannot tell which client is which.
 *
 * Kept here so the picker, listing column, and CRM card render
 * consistently. Add new sites to call this helper rather than
 * re-implement the heuristic inline.
 */
export function clientDisplayLabel(
  client: Pick<Client, 'name' | 'identificationType' | 'identificationNumber'>,
): string {
  const name = client.name?.trim();
  if (!name) return client.identificationNumber;
  const upper = name.toUpperCase();
  if (upper === client.identificationNumber.toUpperCase()) return client.identificationNumber;
  if (client.identificationType && upper === client.identificationType.toUpperCase()) {
    return client.identificationNumber;
  }
  return name;
}
